<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

import type { ManageRewardsRole } from './types';

const props = defineProps<{
  open: boolean;
  role: ManageRewardsRole | null;
}>();

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const root = ref<HTMLElement | null>(null);

const panelClass = computed(() => {
  const role = props.role;
  const isWide = role === 'reward' || role === 'reward-purchased';
  const hasVerticalPaddingOnly = role === 'reward-purchased';

  return [
    isWide ? 'w-[520px]' : 'max-w-sm',
    hasVerticalPaddingOnly ? 'py-6' : 'p-6',
  ];
});
</script>

<template>
  <TransitionRoot as="template" :show="open" data-cy="modal">
    <Dialog
      :initial-focus="root"
      as="div"
      class="relative z-10"
      @close="emit('close')"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-background-emphasis bg-opacity-70 backdrop-blur-md transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div ref="root" class="flex min-h-full items-center justify-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="mx-4 transform overflow-hidden rounded-[15px] bg-background-default shadow-xl transition-all md:mx-auto"
              :class="panelClass"
            >
              <slot />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
